import { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Tabs,
    Tab
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Replay as ResubmitIcon,
    AttachFile as AttachFileIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import { fetchMyBusinessCases, BusinessCaseClient, updateBusinessCase } from '../services/businessCase';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

const MyBusinessCasesPage = () => {
    const navigate = useNavigate();
    const [businessCases, setBusinessCases] = useState<BusinessCaseClient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCase, setSelectedCase] = useState<BusinessCaseClient | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadBusinessCases();
    }, []);

    const loadBusinessCases = async () => {
        try {
            setLoading(true);
            const principal = await getClientPrincipal();
            if (!principal?.userDetails) {
                throw new Error('User not authenticated');
            }

            const cases = await fetchMyBusinessCases(principal.userDetails);
            setBusinessCases(cases);
        } catch (err) {
            console.error('Error loading business cases:', err);
            setError('Failed to load business cases');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (businessCase: BusinessCaseClient) => {
        setSelectedCase(businessCase);
        setDetailsDialogOpen(true);
    };

    const handleEdit = (businessCase: BusinessCaseClient) => {
        // Navigate to form with case ID for editing
        navigate(`/business-case-form?edit=${businessCase.id}`);
    };

    const handleResubmit = (businessCase: BusinessCaseClient) => {
        // Navigate to form with case ID for editing/resubmission
        navigate(`/business-case-form?edit=${businessCase.id}&resubmit=true`);
    };

    const handleDeleteClick = (businessCase: BusinessCaseClient) => {
        setSelectedCase(businessCase);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedCase) return;

        try {
            setDeleting(true);
            // Soft delete by updating status to 'deleted'
            await updateBusinessCase(selectedCase.id!, { status: 'deleted' });
            
            // Reload cases
            await loadBusinessCases();
            setDeleteDialogOpen(false);
            setDetailsDialogOpen(false);
        } catch (err) {
            console.error('Error deleting business case:', err);
            alert('Failed to delete business case');
        } finally {
            setDeleting(false);
        }
    };

    const getStatusColor = (status: string): "default" | "warning" | "success" | "error" | "info" => {
        switch (status) {
            case 'draft':
                return 'info';
            case 'pending':
                return 'warning';
            case 'approved':
                return 'success';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const draftCases = businessCases.filter(bc => bc.status === 'draft');
    const pendingCases = businessCases.filter(bc => bc.status === 'pending');
    const historicalCases = businessCases.filter(bc => bc.status !== 'pending' && bc.status !== 'draft' && bc.status !== 'deleted');

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#009688', mb: 3 }}>
                    My Business Cases
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab label={`Drafts (${draftCases.length})`} />
                        <Tab label={`Pending (${pendingCases.length})`} />
                        <Tab label={`Historical (${historicalCases.length})`} />
                    </Tabs>
                </Box>

                {/* Drafts Tab */}
                <TabPanel value={tabValue} index={0}>
                    {draftCases.length === 0 ? (
                        <Alert severity="info">No draft business cases</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Opportunity Name</TableCell>
                                        <TableCell>Client</TableCell>
                                        <TableCell>Last Updated</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {draftCases.map((bc) => (
                                        <TableRow key={bc.id}>
                                            <TableCell>{bc.opportunity_name || '(Untitled)'}</TableCell>
                                            <TableCell>{bc.client || '-'}</TableCell>
                                            <TableCell>
                                                {bc.updated_at ? new Date(bc.updated_at).toLocaleDateString() : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label="DRAFT"
                                                    color="info"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(bc)}
                                                    title="Continue Editing"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(bc)}
                                                    title="Delete Draft"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                {/* Pending Cases Tab */}
                <TabPanel value={tabValue} index={1}>
                    {pendingCases.length === 0 ? (
                        <Alert severity="info">No pending business cases</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Opportunity Name</TableCell>
                                        <TableCell>Client</TableCell>
                                        <TableCell>Submitted</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pendingCases.map((bc) => (
                                        <TableRow key={bc.id}>
                                            <TableCell>{bc.opportunity_name}</TableCell>
                                            <TableCell>{bc.client}</TableCell>
                                            <TableCell>
                                                {bc.submission_date ? new Date(bc.submission_date).toLocaleDateString() : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={bc.status.toUpperCase()}
                                                    color={getStatusColor(bc.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(bc)}
                                                    title="View Details"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(bc)}
                                                    title="Edit"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(bc)}
                                                    title="Delete"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                {/* Historical Cases Tab */}
                <TabPanel value={tabValue} index={2}>
                    {historicalCases.length === 0 ? (
                        <Alert severity="info">No historical business cases</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Opportunity Name</TableCell>
                                        <TableCell>Client</TableCell>
                                        <TableCell>Submitted</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Decision Date</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historicalCases.map((bc) => (
                                        <TableRow key={bc.id}>
                                            <TableCell>{bc.opportunity_name}</TableCell>
                                            <TableCell>{bc.client}</TableCell>
                                            <TableCell>
                                                {bc.submission_date ? new Date(bc.submission_date).toLocaleDateString() : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={bc.status.toUpperCase()}
                                                    color={getStatusColor(bc.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {bc.approval_date ? new Date(bc.approval_date).toLocaleDateString() : 'N/A'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(bc)}
                                                    title="View Details"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                                {bc.status === 'rejected' && (
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleResubmit(bc)}
                                                        title="Resubmit"
                                                    >
                                                        <ResubmitIcon />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(bc)}
                                                    title="Edit"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(bc)}
                                                    title="Delete"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Business Case</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this business case? This action cannot be undone.
                    </Typography>
                    {selectedCase && (
                        <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                            {selectedCase.opportunity_name}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Details Dialog */}
            <Dialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Business Case Details</DialogTitle>
                <DialogContent>
                    {selectedCase && (
                        <Box sx={{ pt: 2 }}>
                            <Typography variant="h6" gutterBottom>{selectedCase.opportunity_name}</Typography>
                            
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">Client</Typography>
                                <Typography variant="body1">{selectedCase.client}</Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">Proposal Coordinator</Typography>
                                <Typography variant="body1">{selectedCase.proposal_coordinator}</Typography>
                            </Box>

                            {selectedCase.country_region && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Country/Region</Typography>
                                    <Typography variant="body1">{selectedCase.country_region}</Typography>
                                </Box>
                            )}

                            {selectedCase.budget && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Budget</Typography>
                                    <Typography variant="body1">{selectedCase.budget}</Typography>
                                </Box>
                            )}

                            {selectedCase.deadline && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Deadline</Typography>
                                    <Typography variant="body1">
                                        {new Date(selectedCase.deadline).toLocaleDateString()}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">Status</Typography>
                                <Chip
                                    label={selectedCase.status.toUpperCase()}
                                    color={getStatusColor(selectedCase.status)}
                                    size="small"
                                />
                            </Box>

                            {selectedCase.approved_by && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Approved By</Typography>
                                    <Typography variant="body1">{selectedCase.approved_by}</Typography>
                                </Box>
                            )}

                            {selectedCase.rejection_reason && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Rejection Reason</Typography>
                                    <Alert severity="warning">{selectedCase.rejection_reason}</Alert>
                                </Box>
                            )}

                            {selectedCase.workstream && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Workstream</Typography>
                                    <Typography variant="body1">{selectedCase.workstream}</Typography>
                                </Box>
                            )}

                            {selectedCase.strategic_case && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Strategic Case</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.strategic_case}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.competitive_advantage && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Competitive Advantage</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.competitive_advantage}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.corporate_experience && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Corporate Experience</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.corporate_experience}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.prospective_team_configuration && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Prospective Team Configuration</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.prospective_team_configuration}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.bidding_capacity && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Bidding Capacity</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.bidding_capacity}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.partnership && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Partnership</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.partnership}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.commercial_case && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Commercial Case</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.commercial_case}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.other_risks && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Other Risks</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.other_risks}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.conflict_of_interest && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Conflicts of Interest</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.conflict_of_interest}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.learning_development && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Learning & Development</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.learning_development}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.gender_equality_social_inclusion && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Gender Equality & Social Inclusion</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedCase.gender_equality_social_inclusion}
                                    </Typography>
                                </Box>
                            )}

                            {selectedCase.attachment_filename && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Attachment</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <AttachFileIcon fontSize="small" color="primary" />
                                        <Typography variant="body1">{selectedCase.attachment_filename}</Typography>
                                        {selectedCase.attachment_size && (
                                            <Typography variant="body2" color="text.secondary">
                                                ({(selectedCase.attachment_size / 1024).toFixed(1)} KB)
                                            </Typography>
                                        )}
                                        {selectedCase.attachment_data && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<DownloadIcon />}
                                                onClick={() => {
                                                    const link = document.createElement('a');
                                                    link.href = selectedCase.attachment_data!;
                                                    link.download = selectedCase.attachment_filename!;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }}
                                            >
                                                Download
                                            </Button>
                                        )}
                                        {!selectedCase.attachment_data && (
                                            <Typography variant="body2" color="error">
                                                (File not available)
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
                    {selectedCase && selectedCase.status === 'pending' && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                setDetailsDialogOpen(false);
                                handleEdit(selectedCase);
                            }}
                        >
                            Edit
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default MyBusinessCasesPage;
